import { autoinject, bindable } from 'aurelia-framework';
import { ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { camelCase } from 'lodash';
import { ResourceService } from '../../../../utils/resourceService';
import { IValidationErrorsAM } from '../../../../utils/validation';
import { CostCenter, CostCenterService } from '../../cost-centers/cost-center-api';
import { OrganizationalNodeModel } from '../organizational-unit-service';
import * as moment from 'moment';
import { CostCenterDateEdit } from './cost-center-date-edit';


@autoinject
export class EditCostCenter {
	@bindable costCenter: OrganizationalNodeModel;
	@bindable updatingCostCenter: (params: any) => Promise<Response>;
	@bindable removingCostCenter: (params: any) => Promise<Response>;

	costCenterModel: CostCenter;
	validationController: ValidationController;
	costCenterDateEdit: CostCenterDateEdit;

	constructor(
		validationControllerFactory: ValidationControllerFactory,
		private resourceService: ResourceService,
		private costCenterService: CostCenterService) {

		this.validationController = validationControllerFactory.createForCurrentScope();
		this.costCenterDateEdit = new CostCenterDateEdit(null);
	}

	async costCenterChanged() {
		if (this.costCenter == null) {
			this.costCenterModel = new CostCenter({ identifier: null, name: null, activeFrom: null, activeUntil: null });
		} else {
			this.costCenterModel = new CostCenter({
				id: this.costCenter.id,
				identifier: this.costCenter.identifier,
				name: this.costCenter.name,
				activeFrom: this.costCenter.activeFrom,
				activeUntil: this.costCenter.activeUntil
			});

			if (this.costCenter.removable == null) {
				this.costCenter.removable = !(await this.costCenterService.hasDependencies(this.costCenter.id));
			}

			this.costCenterModel.removable = this.costCenter.removable;
		}

		this.costCenterDateEdit.setCostCenter(this.costCenterModel);
		this.attachValidationRules(this.costCenterModel);
		this.validationController.reset();
	}

	async submit() {
		const result = await this.validationController.validate({ object: this.costCenterModel });
		if (!result.valid) {
			return;
		}

		const response = await this.updatingCostCenter({ costCenter: this.costCenterModel.toAM() });

		if (response.status === 200) {
			this.handleSubmitSuccess();
		} else {
			await this.handleSubmitError(response);
		}
	}

	async remove() {
		await this.removingCostCenter({ costCenter: this.costCenterModel.toAM() });
	}

	private handleSubmitSuccess() {
		this.attachValidationRules(this.costCenterModel);
	}

	private async handleSubmitError(response: Response) {
		const errors = await response.json() as IValidationErrorsAM;
		for (const key in errors) {
			if (!errors.hasOwnProperty(key)) { continue; }
			errors[key].forEach(message => {
				this.validationController.addError(
					this.resourceService.getResource(message),
					this.costCenterModel,
					camelCase(key)
				);
			});
		}
	}

	private attachValidationRules(costCenter: CostCenter) {
		ValidationRules
			.ensure((c: CostCenter) => c.identifier)
				.required()
				.withMessageKey('CostCenter_ValidationError_IdentifierIsRequired')
			.ensure((c: CostCenter) => c.name)
				.required()
				.withMessageKey('CostCenter_ValidationError_NameIsRequired')
			.on(this.costCenterModel);
	}		
}
